import { RiCheckLine, RiCloseLine, RiSubtractLine } from "@remixicon/react";
import { formatBerlin } from "@/lib/datetime";
import { cn } from "@/lib/utils";

/**
 * The five things that happen to an order, in the order they happen.
 *
 * Chronological on purpose, which is what makes left-to-right honest:
 * allocation and reservation land seconds apart, the store mail follows
 * immediately, and `inTransferBranch` arrives roughly half an hour later (2h
 * observed) because Prohandel applies the transfer on its own schedule. That
 * last step is therefore the one most likely to sit unfinished, and the bar
 * exists largely so an operator can see it hanging.
 *
 * Replaces the old Prohandel + Status badges, which showed two end-states and
 * hid the pipeline between them.
 */
export interface OrderProgressProps {
	dateCreated: string;
	allocatedAt: string | null;
	reservedAt: string | null;
	notifiedAt: string | null;
	inTransferBranchAt: string | null;
	/** Prohandel booked the SALE out of the transfer branch. The final step:
	 *  until this lands the goods show as transferred-but-never-sold in the ERP,
	 *  which is exactly what the operator reported on 2026-08-27. */
	issuedAt: string | null;
	/** No transfer branch configured: the ERP step can never complete, so it is
	 *  n/a rather than pending. */
	transferNotApplicable: boolean;
	/** Drives the red marker: which step, if any, has actually failed. */
	fulfillmentStatus: string;
}

type StepState = "done" | "pending" | "failed" | "na" | "cancelled";

interface Step {
	key: string;
	label: string;
	at: string | null;
	state: StepState;
	/** Shown in the tooltip when a step is not simply done. */
	note?: string;
}

/**
 * A failed order stops the bar at the step that failed, rather than showing
 * every later step as merely pending - "pending" implies it is still coming.
 */
function failureStep(status: string): { index: number; note: string } | null {
	switch (status) {
		case "needs_branch":
			return { index: 1, note: "Keine Filiale zugewiesen - manuell zuweisen" };
		case "no_single_branch":
			return { index: 1, note: "Keine Filiale hat alle Artikel" };
		case "partial_stock":
			return { index: 1, note: "Für mindestens eine Position fehlt Bestand" };
		// NOTE: "cancelled" is deliberately absent here - it is not a failure of
		// one step but a state of the whole order, handled in buildSteps so every
		// step renders as cancelled rather than one red cross and four "pending"
		// dots that imply the order is still coming.
		default:
			return null;
	}
}

export function buildSteps(p: OrderProgressProps): Step[] {
	const failure = failureStep(p.fulfillmentStatus);

	const raw: Omit<Step, "state">[] = [
		{ key: "received", label: "Eingegangen", at: p.dateCreated },
		{ key: "allocated", label: "Zugewiesen", at: p.allocatedAt },
		{ key: "reserved", label: "Reserviert", at: p.reservedAt },
		{ key: "notified", label: "Laden informiert", at: p.notifiedAt },
		{ key: "in600", label: "In Transferfiliale", at: p.inTransferBranchAt },
		{ key: "sold", label: "Verkauft", at: p.issuedAt },
	];

	// A cancelled order is not "in progress with a failed step": Farfetch called
	// it off, so nothing further is coming and no step is actionable. Showing
	// later steps as merely "pending" would tell an operator to wait for
	// something that will never happen.
	if (p.fulfillmentStatus === "cancelled") {
		return raw.map((step) => ({
			...step,
			state: "cancelled" as StepState,
			note: "Bestellung storniert",
		}));
	}

	return raw.map((step, i) => {
		if (failure && i === failure.index && !step.at) {
			return { ...step, state: "failed", note: failure.note };
		}
		// Without a transfer branch there is no 600 to move into and therefore no
		// sale to book out of it - both steps are n/a, not stuck.
		if ((step.key === "in600" || step.key === "sold") && p.transferNotApplicable) {
			// Not a stuck step: this org moves no stock into a transfer branch.
			return { ...step, state: "na", note: "Keine Transferfiliale konfiguriert" };
		}
		return { ...step, state: step.at ? "done" : "pending" };
	});
}

// Colour carries the state at a glance across a table of 20 rows, so each one
// must be distinguishable from the others without reading the tooltip:
//   green  done      - nothing to do
//   amber  pending   - outstanding, still expected to happen
//   red    failed    - something went wrong on THIS step; act on it
//   red    cancelled - the order is off; also red, but with no action implied
//   grey   n/a       - cannot apply to this org, dashed so it reads as "absent"
//                      rather than "waiting"
// Pending was grey before, which made an outstanding step look the same as one
// that does not apply.
const TONE: Record<StepState, string> = {
	done: "bg-emerald-600 text-white border-emerald-600",
	pending: "bg-amber-100 text-amber-700 border-amber-300",
	failed: "bg-destructive text-white border-destructive",
	na: "bg-muted/50 text-muted-foreground/60 border-dashed border-border",
	cancelled: "bg-destructive text-white border-destructive",
};

/**
 * Compact five-step bar for a table row.
 *
 * Each marker carries its own `title`, so hovering any step shows what it is
 * and exactly when it happened (Berlin time) - the timestamps are the point,
 * and a row is too narrow to print five of them.
 */
/// The first step that is not done - i.e. what someone is waiting on.
///
/// Returns null when every step is complete. A failed step wins over a merely
/// pending one, because "no branch has stock" is what an operator must act on,
/// not "the mail has not gone out".
export function nextStep(props: OrderProgressProps): { label: string; failed: boolean } | null {
	// Nothing is pending on a cancelled order, and calling it "failed" would put
	// it in the operator's action list. It is simply over.
	if (props.fulfillmentStatus === "cancelled") {
		return { label: "Storniert", failed: false };
	}
	const steps = buildSteps(props);
	const failed = steps.find((s) => s.state === "failed");
	if (failed) return { label: failed.note ?? failed.label, failed: true };
	const pending = steps.find((s) => s.state === "pending");
	return pending ? { label: pending.label, failed: false } : null;
}

export function OrderProgress(props: OrderProgressProps) {
	const steps = buildSteps(props);
	const summary = steps
		.map((s) => `${s.label}: ${s.at ? formatBerlin(s.at) : (s.note ?? "offen")}`)
		.join(". ");

	return (
		// role="img" with the whole summary as its label. A bare <div> ignores
		// aria-label entirely, and role="group" wants a <fieldset>, which is for
		// form controls. This bar IS a graphic conveying one fact - how far the
		// order has got - so an image with alt text is the honest description,
		// and it replaces the separate sr-only span that said the same thing.
		// `title` on the CONTAINER, not only on each dot: with role="img" the
		// aria-label subsumes the children, and browsers stopped surfacing the
		// per-dot titles on hover. One tooltip on the whole bar always works, and
		// the new "Nächster Schritt" column carries the same information as text
		// for anyone who does not hover at all.
		<div
			className="flex items-center"
			role="img"
			aria-label={summary}
			title={summary.replace(/\. /g, "\n")}
		>
			{steps.map((step, i) => (
				<div key={step.key} className="flex items-center">
					{i > 0 && (
						<span
							aria-hidden
							className={cn(
								"h-px w-3 sm:w-5",
								step.state === "done" ? "bg-emerald-600" : "bg-border",
							)}
						/>
					)}
					<span
						title={
							step.at
								? `${step.label}: ${formatBerlin(step.at)}`
								: `${step.label}: ${step.note ?? "offen"}`
						}
						className={cn(
							"flex size-5 items-center justify-center rounded-full border text-[10px]",
							TONE[step.state],
						)}
					>
						{step.state === "done" && <RiCheckLine className="size-3" aria-hidden />}
						{step.state === "failed" && <RiCloseLine className="size-3" aria-hidden />}
						{step.state === "na" && <RiSubtractLine className="size-3" aria-hidden />}
					</span>
				</div>
			))}
		</div>
	);
}

/** The six step labels, in order - the table renders one column per entry. */
export const STEP_LABELS = [
	"Eingegangen",
	"Zugewiesen",
	"Reserviert",
	"Laden informiert",
	"In Transferfiliale",
	"Verkauft",
] as const;

/**
 * One marker per step, for a table that gives every step its own column.
 *
 * Replaces the single "Fortschritt" column, where six dots sat under one
 * heading and the operator had to hover to learn which was which. With a column
 * per step the heading IS the label, so the meaning is readable at a glance and
 * a whole column of gaps shows immediately where orders pile up.
 *
 * Returns cells rather than a wrapping element: the caller places each one in
 * its own <TableCell>, which a nested component could not do.
 */
export function OrderProgressCells(props: OrderProgressProps) {
	const steps = buildSteps(props);
	return steps.map((step) => (
		<span
			key={step.key}
			title={
				step.at
					? `${step.label}: ${formatBerlin(step.at)}`
					: `${step.label}: ${step.note ?? "offen"}`
			}
			aria-label={
				step.at
					? `${step.label}: ${formatBerlin(step.at)}`
					: `${step.label}: ${step.note ?? "offen"}`
			}
			role="img"
			className={cn(
				"flex size-5 items-center justify-center rounded-full border text-[10px]",
				TONE[step.state],
			)}
		>
			{step.state === "done" && <RiCheckLine className="size-3" aria-hidden />}
			{step.state === "failed" && <RiCloseLine className="size-3" aria-hidden />}
			{step.state === "na" && <RiSubtractLine className="size-3" aria-hidden />}
			{step.state === "cancelled" && <RiCloseLine className="size-3" aria-hidden />}
		</span>
	));
}
